import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import classes from './FileUpload.scss';
import { ensureArray } from 'cityads-v3/utils/arrayUtils';
import { approxFormatNumber, approxFormatBinaryNumber } from 'cityads-v3/utils/textUtils';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dropzone from 'react-dropzone';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import DownloadButton from 'cityads-v3/components/Buttons/DownloadButton';

import config from 'cityads-v3/config';

export default function FileUpload({
  value,
  onChange,
  multiple = false,
  maxLength = Infinity,
  maxSize = Infinity,
  minSize = 0,
  showFileList = true,
  disabled = false,
  ...rest
}) {
  const { t } = useTranslation(['main']);

  const [files, setFiles] = useState([]);
  const [rejectedFiles, setRejectedFiles] = useState([]);

  const rejectedFilesByReason = useMemo(() => {
    return rejectedFiles.reduce((acc, file, i, rejectedFiles) => {
      if (!multiple && rejectedFiles.length > 1) {
        acc.tooMany.push(file);
      } else if (file.size > maxSize) {
        acc.tooLarge.push(file);
      } else if (file.size < minSize) {
        acc.tooSmall.push(file);
      } else {
        // size constraints satisfied, MIME mismatch
        acc.wrongFormat.push(file);
      }

      return acc;
    }, { tooMany: [], tooLarge: [], tooSmall: [], wrongFormat: [] });
  }, [rejectedFiles, multiple, maxSize, minSize]);

  useEffect(() => {
    setFiles(value ? ensureArray(value) : []);
  }, [value]);

  const handleDrop = useCallback((acceptedFiles, rejectedFiles) => {
    acceptedFiles = acceptedFiles.slice(0, maxLength);

    setFiles(acceptedFiles);
    setRejectedFiles(rejectedFiles);

    onChange(acceptedFiles);
  }, [maxLength, setFiles, setRejectedFiles, onChange]);

  const removeFile = useCallback(num => {
    const newFiles = [...files];

    newFiles.splice(num, 1);

    setFiles(newFiles);
    onChange(newFiles);
  }, [files, setFiles, onChange]);

  const handleAlertClose = useCallback(() => {
    setRejectedFiles([]);
  }, [setRejectedFiles]);

  return (
    <div>
      {files.length < (multiple ? maxLength : 1) && (
        <Dropzone
          onDrop={handleDrop}
          className={classNames(classes.dropzone, { [classes.dropzoneActive]: !disabled })}
          multiple={multiple}
          maxSize={maxSize}
          minSize={minSize}
          disabled={disabled}
          {...rest}
        >
          <CloudUploadOutlinedIcon classes={{ root: classes.iconUpload }} />
          <Typography component="span">
            {multiple ? t('dropzoneMultiple') : t('dropzoneSingle')}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            classes={{
              root: classes.uploadButton
            }}
            disabled={disabled}
          >
            {multiple ? t('dropzoneButtonMultiple') : t('dropzoneButtonSingle')}
          </Button>
        </Dropzone>
      )}
      {showFileList && (
        <div className={classes.fileList}>
          {files.map((file, i) => {
            const fileName = file.name ?? file.fileName;

            return (
              <DownloadButton
                key={i}
                className={classes.file}
                title={fileName}
                size={file.size ? approxFormatNumber(file.size) : null}
                ext={fileName.split('.').reverse()[0]}
                icon={<DeleteOutlinedIcon />}
                iconClick={() => removeFile(i)}
              />
            )
          })}
        </div>
      )}
      <Snackbar open={rejectedFiles.length > 0} autoHideDuration={config.SNACKBAR_AUTOHIDE_DURATION} onClose={handleAlertClose}>
        <Alert severity="error" className={classes.alert}>
          {rejectedFilesByReason.tooMany.length ?
            t('tooManyFiles', {
              count: rejectedFilesByReason.tooMany.length,
            })
            :
            null
          }
          {rejectedFilesByReason.tooLarge.length ?
            t('fileTooLarge', {
              count: rejectedFilesByReason.tooLarge.length,
              fileName: rejectedFilesByReason.tooLarge.map(file => file.name).join(', '),
              maxSize: approxFormatBinaryNumber(maxSize),
            })
            :
            null
          }
          {rejectedFilesByReason.tooSmall.length ?
            t('fileTooSmall', {
              count: rejectedFilesByReason.tooSmall.length,
              fileName: rejectedFilesByReason.tooSmall.map(file => file.name).join(', '),
              minSize: approxFormatBinaryNumber(minSize),
            })
            :
            null
          }
          {rejectedFilesByReason.wrongFormat.length ?
            t('fileWrongFormat', {
              count: rejectedFilesByReason.wrongFormat.length,
              fileName: rejectedFilesByReason.wrongFormat.map(file => file.name).join(', '),
              accept: rest.accept,
            })
            :
            null
          }
        </Alert>
      </Snackbar>
    </div>
  );
}

FileUpload.propTypes = {
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  maxLength: PropTypes.number,
  showFileList: PropTypes.bool
};
